import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { X, Search, RotateCcw } from "lucide-react";
import { ProfileFilters as FiltersType } from "@/hooks/useMatchmakerProfiles";
import { useProfileFilterOptions } from "@/hooks/useProfileFilterOptions";

interface ProfileFiltersProps {
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
  resultsCount: number;
}

export function ProfileFilters({ filters, onFiltersChange, resultsCount }: ProfileFiltersProps) {
  const { options, loading: optionsLoading } = useProfileFilterOptions();

  const updateFilter = (key: keyof FiltersType, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleArrayFilter = (key: "locations" | "religiosityLevels" | "educationLevels" | "ethnicities" | "maritalStatuses" | "citizenshipStatuses" | "sects", value: string) => {
    const current = filters[key];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateFilter(key, updated);
  };

  const resetFilters = () => {
    onFiltersChange({
      search: "",
      gender: undefined,
      ageMin: 18,
      ageMax: 60,
      locations: [],
      religiosityLevels: [],
      educationLevels: [],
      ethnicities: [],
      maritalStatuses: [],
      citizenshipStatuses: [],
      sects: [],
      activeOnly: false,
      status: "all",
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.gender ||
    filters.locations.length > 0 ||
    filters.religiosityLevels.length > 0 ||
    filters.educationLevels.length > 0 ||
    filters.ethnicities.length > 0 ||
    filters.maritalStatuses.length > 0 ||
    filters.citizenshipStatuses.length > 0 ||
    filters.sects.length > 0 ||
    filters.activeOnly ||
    (filters.status && filters.status !== "all") ||
    filters.ageMin !== 18 ||
    filters.ageMax !== 60;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Search & Filter</CardTitle>
            <CardDescription>
              {resultsCount} {resultsCount === 1 ? "profile" : "profiles"} found
            </CardDescription>
          </div>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={resetFilters}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Search */}
        <div className="space-y-2">
          <Label>Search by Name</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Enter first name..."
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Gender Toggle */}
        <div className="space-y-2">
          <Label>Gender</Label>
          <div className="flex gap-2">
            <Button
              variant={filters.gender === "male" ? "default" : "outline"}
              size="sm"
              onClick={() => updateFilter("gender", filters.gender === "male" ? undefined : "male")}
            >
              Male
            </Button>
            <Button
              variant={filters.gender === "female" ? "default" : "outline"}
              size="sm"
              onClick={() => updateFilter("gender", filters.gender === "female" ? undefined : "female")}
            >
              Female
            </Button>
          </div>
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <Label>Profile Status</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={filters.status === "all" || !filters.status ? "default" : "outline"}
              size="sm"
              onClick={() => updateFilter("status", "all")}
            >
              All
            </Button>
            <Button
              variant={filters.status === "active" ? "default" : "outline"}
              size="sm"
              onClick={() => updateFilter("status", "active")}
            >
              Active
            </Button>
            <Button
              variant={filters.status === "pending" ? "default" : "outline"}
              size="sm"
              onClick={() => updateFilter("status", "pending")}
            >
              Pending
            </Button>
            <Button
              variant={filters.status === "declined" ? "default" : "outline"}
              size="sm"
              onClick={() => updateFilter("status", "declined")}
            >
              Declined
            </Button>
          </div>
        </div>

        {/* Age Range */}
        <div className="space-y-2">
          <Label>Age Range: {filters.ageMin || 18} - {filters.ageMax || 60}</Label>
          <div className="px-2">
            <Slider
              min={18}
              max={60}
              step={1}
              value={[filters.ageMin || 18, filters.ageMax || 60]}
              onValueChange={([min, max]) => {
                onFiltersChange({ ...filters, ageMin: min, ageMax: max });
              }}
              minStepsBetweenThumbs={1}
              className="w-full"
            />
          </div>
        </div>

        {optionsLoading ? (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </>
        ) : (
          <>
            {/* Location */}
            {options.locations.length > 0 && (
              <div className="space-y-2">
                <Label>Location {filters.locations.length > 0 && `(${filters.locations.length})`}</Label>
                <div className="flex flex-wrap gap-2">
                  {options.locations.map((location) => (
                    <Badge
                      key={location}
                      variant={filters.locations.includes(location) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleArrayFilter("locations", location)}
                    >
                      {location}
                      {filters.locations.includes(location) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Religiosity Level */}
            {options.religiosityLevels.length > 0 && (
              <div className="space-y-2">
                <Label>Religiosity Level {filters.religiosityLevels.length > 0 && `(${filters.religiosityLevels.length})`}</Label>
                <div className="space-y-2">
                  {options.religiosityLevels.map((level) => (
                    <div key={level} className="flex items-center space-x-2">
                      <Checkbox
                        id={`religiosity-${level}`}
                        checked={filters.religiosityLevels.includes(level)}
                        onCheckedChange={() => toggleArrayFilter("religiosityLevels", level)}
                      />
                      <Label
                        htmlFor={`religiosity-${level}`}
                        className="font-normal cursor-pointer capitalize"
                      >
                        {level}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education Level */}
            {options.educationLevels.length > 0 && (
              <div className="space-y-2">
                <Label>Education Level {filters.educationLevels.length > 0 && `(${filters.educationLevels.length})`}</Label>
                <div className="space-y-2">
                  {options.educationLevels.map((level) => (
                    <div key={level} className="flex items-center space-x-2">
                      <Checkbox
                        id={`education-${level}`}
                        checked={filters.educationLevels.includes(level)}
                        onCheckedChange={() => toggleArrayFilter("educationLevels", level)}
                      />
                      <Label
                        htmlFor={`education-${level}`}
                        className="font-normal cursor-pointer"
                      >
                        {level}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ethnicity */}
            {options.ethnicities.length > 0 && (
              <div className="space-y-2">
                <Label>Ethnicity {filters.ethnicities.length > 0 && `(${filters.ethnicities.length})`}</Label>
                <div className="space-y-2">
                  {options.ethnicities.map((ethnicity) => (
                    <div key={ethnicity} className="flex items-center space-x-2">
                      <Checkbox
                        id={`ethnicity-${ethnicity}`}
                        checked={filters.ethnicities.includes(ethnicity)}
                        onCheckedChange={() => toggleArrayFilter("ethnicities", ethnicity)}
                      />
                      <Label
                        htmlFor={`ethnicity-${ethnicity}`}
                        className="font-normal cursor-pointer"
                      >
                        {ethnicity}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Marital Status */}
            {options.maritalStatuses.length > 0 && (
              <div className="space-y-2">
                <Label>Marital Status {filters.maritalStatuses.length > 0 && `(${filters.maritalStatuses.length})`}</Label>
                <div className="space-y-2">
                  {options.maritalStatuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`marital-${status}`}
                        checked={filters.maritalStatuses.includes(status)}
                        onCheckedChange={() => toggleArrayFilter("maritalStatuses", status)}
                      />
                      <Label
                        htmlFor={`marital-${status}`}
                        className="font-normal cursor-pointer capitalize"
                      >
                        {status}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Citizenship Status */}
            {options.citizenshipStatuses.length > 0 && (
              <div className="space-y-2">
                <Label>Citizenship {filters.citizenshipStatuses.length > 0 && `(${filters.citizenshipStatuses.length})`}</Label>
                <div className="space-y-2">
                  {options.citizenshipStatuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`citizenship-${status}`}
                        checked={filters.citizenshipStatuses.includes(status)}
                        onCheckedChange={() => toggleArrayFilter("citizenshipStatuses", status)}
                      />
                      <Label
                        htmlFor={`citizenship-${status}`}
                        className="font-normal cursor-pointer"
                      >
                        {status}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sect */}
            {options.sects.length > 0 && (
              <div className="space-y-2">
                <Label>Sect {filters.sects.length > 0 && `(${filters.sects.length})`}</Label>
                <div className="space-y-2">
                  {options.sects.map((sect) => (
                    <div key={sect} className="flex items-center space-x-2">
                      <Checkbox
                        id={`sect-${sect}`}
                        checked={filters.sects.includes(sect)}
                        onCheckedChange={() => toggleArrayFilter("sects", sect)}
                      />
                      <Label
                        htmlFor={`sect-${sect}`}
                        className="font-normal cursor-pointer capitalize"
                      >
                        {sect}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Active Status */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label>Active Profiles Only</Label>
            <p className="text-xs text-muted-foreground">
              Show only profiles marked as active
            </p>
          </div>
          <Switch
            checked={filters.activeOnly}
            onCheckedChange={(checked) => updateFilter("activeOnly", checked)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
